import { z } from 'zod';

import { i18n } from '@/lib/i18n';

const getRequiredFieldSchema = (field: string) =>
  i18n.t('required-field', { field: i18n.t(field) });

const getRequiredStringFieldSchema = (field: string) =>
  z
    .string({
      required_error: getRequiredFieldSchema(field),
      invalid_type_error: getRequiredFieldSchema(field),
    })
    .min(1, getRequiredFieldSchema(field));

const getRequiredNumberFieldSchema = (field: string) =>
  z.number({
    required_error: getRequiredFieldSchema(field),
    invalid_type_error: getRequiredFieldSchema(field),
  });

export const getEstateValidationSchema = ({ propertyType }: { propertyType?: string }) => {
  const estateSchema = z.object({
    name: z
      .string()
      .max(60, i18n.t('fieldMustBeAtMostCharacters', { field: i18n.t('name'), max: 60 })),
    address: z.object({
      address: getRequiredStringFieldSchema('address'),
      houseNumber: getRequiredStringFieldSchema('houseNumber'),
      street: getRequiredStringFieldSchema('street'),
      postalCode: getRequiredStringFieldSchema('postalCode'),
      city: getRequiredStringFieldSchema('city'),
      countryId: getRequiredStringFieldSchema('country'),
    }),
    estateDetails: z.object({
      marketType: getRequiredStringFieldSchema('marketType'),
      propertyType: z.enum(['house', 'apartment'], {
        errorMap: () => ({
          message: i18n.t('analyticsOnlyForSpecifiedPropertyTypes'),
        }),
      }),
      buildingYear: getRequiredNumberFieldSchema('buildingYear'),
    }),
    estateAreasRooms: z.object({
      livingArea: getRequiredNumberFieldSchema('livingArea')
        .min(10, i18n.t('fieldMustBeAtLeast', { field: i18n.t('livingArea'), min: 10 }))
        .max(800, i18n.t('fieldMustBeAtMost', { field: i18n.t('livingArea'), max: 800 })),
      totalNumberOfRooms: getRequiredNumberFieldSchema('totalNumberOfRooms')
        .min(1, getRequiredFieldSchema('totalNumberOfRooms'))
        .max(100, i18n.t('fieldMustBeAtMost', { field: i18n.t('totalNumberOfRooms'), max: 100 })),
      numberOfBathrooms: getRequiredNumberFieldSchema('numberOfBathrooms')
        .min(1, i18n.t('fieldMustBeAtLeast', { field: i18n.t('numberOfBathrooms'), min: 1 }))
        .max(5, i18n.t('fieldMustBeAtMost', { field: i18n.t('numberOfBathrooms'), max: 5 })),
      balconyArea: getRequiredNumberFieldSchema('balconyArea').max(
        200,
        i18n.t('fieldMustBeAtMost', { field: i18n.t('balconyArea'), max: 200 })
      ),
      gardenArea: getRequiredNumberFieldSchema('gardenArea').max(
        200,
        i18n.t('fieldMustBeAtMost', { field: i18n.t('gardenArea'), max: 200 })
      ),
      ...(propertyType === 'house'
        ? {
            plotArea: getRequiredNumberFieldSchema('plotArea')
              .min(50, i18n.t('fieldMustBeAtLeast', { field: i18n.t('plotArea'), min: 50 }))
              .max(5000, i18n.t('fieldMustBeAtMost', { field: i18n.t('plotArea'), max: 5000 })),
          }
        : {}),
      ...(propertyType === 'apartment'
        ? {
            floor: getRequiredNumberFieldSchema('floor'),
            hasElevator: getRequiredNumberFieldSchema('hasElevator'),
          }
        : {}),
    }),
    estateEnergyDetails: z.object({
      hwbClass: getRequiredStringFieldSchema('hwbClass'),
    }),
    estateRatings: z.object({
      kitchenQuality: getRequiredNumberFieldSchema('kitchen'),
      kitchenCondition: getRequiredNumberFieldSchema('kitchen'),
      bathroomQuality: getRequiredNumberFieldSchema('bathroom'),
      bathroomCondition: getRequiredNumberFieldSchema('bathroom'),
      flooringQuality: getRequiredNumberFieldSchema('flooring'),
      flooringCondition: getRequiredNumberFieldSchema('flooring'),
      windowsQuality: getRequiredNumberFieldSchema('windows'),
      windowsCondition: getRequiredNumberFieldSchema('windows'),
    }),
  });

  return estateSchema;
};

export type EstateValidationSchemaType = z.infer<ReturnType<typeof getEstateValidationSchema>>;
